import {Authentication} from "../models/interfaces/authentication.interface";
import {AuthenticationResponse} from "../models/interfaces/authentication-response.interface";
import {ResetPasswordBodyRequest} from "../models/interfaces/reset-email-body.interface";
import {AuthenticatedUser} from "../models/interfaces/authenticated-user.interface";
import {ChangePasswordBodyRequest} from "../models/interfaces/change-password-body.interface";
import {RegisterBody} from "../models/interfaces/register-body.interface";
import {CustomAxiosRequestConfig} from "@libs/axios";
import LocalStorageService from "@common/services/local-storage.service";
import {LocalStorageKeys} from "@common/models/enums/local-storage-keys.enum";
import NotificationService from "@common/services/notification.service";
import {AppApiService} from "../../core/services/app-api.service";

class AuthService extends AppApiService {

    private authentication: Authentication | null = this.loadAuthenticationFromLocalStorage();
    private onLogoutCallback: (() => void) | null = null;

    constructor() {
        super();
    }

    public async authenticate(email: string, password: string): Promise<string> {
        const authHeader: string = 'Basic ' + btoa(`${email}:${password}`);
        const httpOptions: CustomAxiosRequestConfig = {
            headers: {
                'Authorization': authHeader
            },
            errorMessage: "Nieprawidłowy login lub hasło.",
            skipDefault401Handling: true,
        };

        if (!this.isAuthenticated()) {
            this.authentication = null;
            LocalStorageService.remove(LocalStorageKeys.AUTHENTICATION);
        }

        return await this.post<AuthenticationResponse>('/authenticate', {}, httpOptions)
            .then((res: AuthenticationResponse): string => {
                this.authentication = this.prepareAuthentication(res);
                LocalStorageService.save(LocalStorageKeys.AUTHENTICATION, this.authentication);
                return "Success"
            })
            .catch(err => {
                throw err
            })
    }

    public async getAuthenticatedUser(): Promise<AuthenticatedUser> {
        return await this.get<AuthenticatedUser>(`/authenticated-user`, {})
    }

    public async registerAccount(body: RegisterBody): Promise<void> {
        return await this.post<void>(`/register`, {password: body.password, email: body.email}, {})
            .then((res) => {
                NotificationService.success('Konto zostało pomyślnie zarejestrowane.')
            })
    }

    public async sendEmailConfirmationToken(email: string): Promise<void> {
        return await this.post<void>('/send-email-confirmation-token', {email}, {
            skipDefault404Handling: true,
            skipDefault401Handling: true,
            skipDefaultErrorHandling: true,
        })
            .catch((err) => {
                NotificationService.error(
                    `Nie udało się wysłać linku aktywacyjnego na podany adres e-mail (${email}).`,
                    err,
                    "",
                    {duration: 7000}
                );
            });
    }

    public async activateAccount(token: string): Promise<void> {
        return await this.put<void>(`/confirm-email/${token}`, {}, {
            skipDefault404Handling: true,
            successMessage: 'Konto zostało aktywowane!',
            errorMessage: "Nie udało się aktywować konta."
        });
    }

    public async updateUsername(username: string): Promise<void> {
        return await this.put<void>(`/profile/update/username/${username}`, {}, {
            skipDefault404Handling: true,
            successMessage: 'Nazwa użytkownika została zaktualizowana.',
            errorMessage: "Nie udało się zaktualizować nazwy użytkownika. Spróbuj ponownie."
        });
    }

    public async getTokenResetPassword(email: string, successMessage?: string): Promise<void> {
        return await this.post<void>('/send-password-reset-token', {email}, {
            skipDefault401Handling: true,
            skipDefault404Handling: true,
            isPublic: true,
            errorMessage: "Nie udało się wysłać kodu resetowania hasła na podany adres e-mail.",
            successMessage: successMessage ?? "Wysłano kod resetowania hasła na podany adres e-mail."
        })
            .then((): void => {
                LocalStorageService.remove(LocalStorageKeys.EMAIL_RESET_PASSWORD);
                LocalStorageService.save(LocalStorageKeys.EMAIL_RESET_PASSWORD, email);
            });
    }

    public async resetPassword(body: ResetPasswordBodyRequest): Promise<void> {
        return await this.put<void>('/reset-password', body, {
            isPublic: true,
            skipDefault401Handling: true,
            skipDefault404Handling: true,
            successMessage: "Twoje hasło zostało zresetowane! Możesz teraz się zalogować.",
            errorMessage: "Wystąpił błąd podczas resetowania hasła. Spróbuj ponownie później."
        })
            .then((): void => {
                LocalStorageService.remove(LocalStorageKeys.EMAIL_RESET_PASSWORD);
            });
    }

    public async changePassword(body: ChangePasswordBodyRequest): Promise<void> {
        return await this.put<void>('/change-password', body, {
            skipDefault401Handling: true,
            skipDefault404Handling: true,
            successMessage: "Twoje hasło zostało zmienione.",
        })
    }

    public get authenticatedUser(): AuthenticatedUser | undefined {
        if (!this.authentication?.user) return undefined;

        return {
            ...this.authentication.user,
        };
    }

    public async logout(): Promise<void> {
        this.authentication = null;
        LocalStorageService.remove(LocalStorageKeys.AUTHENTICATION);

        if (this.onLogoutCallback) {
            try {
                this.onLogoutCallback();
            } catch (error) {
                console.error("Error in onLogoutCallback:", error);
            }
        }
    }

    public setLogoutCallback(callback: (() => void) | null) {
        this.onLogoutCallback = callback;
    }

    public isAuthenticated(): boolean {
        return !!this.authentication && new Date(this.authentication.token.validUntil).getTime() > new Date().getTime();
    }

    public updateAuthenticationField<K extends keyof Authentication>(
        key: K,
        value: Authentication[K]
    ): void {
        if (this.authentication) {
            this.authentication = {
                ...this.authentication,
                [key]: value
            };
            LocalStorageService.remove(LocalStorageKeys.AUTHENTICATION);
            LocalStorageService.save(LocalStorageKeys.AUTHENTICATION, this.authentication);
        }
    }

    public prepareTokenValidUntil(tokenValidInMinutes: number): Date {
        return new Date(new Date().getTime() + tokenValidInMinutes * 60 * 1000);
    }

    public get getAuthToken(): string | undefined {
        const authentication = LocalStorageService.get(LocalStorageKeys.AUTHENTICATION);
        if (authentication) {
            return authentication?.token.value;
        }
        return undefined;
    }

    private loadAuthenticationFromLocalStorage(): Authentication | null {
        const authentication = LocalStorageService.get<Authentication>(LocalStorageKeys.AUTHENTICATION) as Authentication;
        if (authentication) {
            return authentication;
        } else {
            LocalStorageService.remove(LocalStorageKeys.AUTHENTICATION);
            return null;
        }
    }

    private prepareAuthentication(authenticationResponse: AuthenticationResponse): Authentication {
        return {
            user: {
                id: authenticationResponse.user.id,
                email: authenticationResponse.user.email,
                role: authenticationResponse.user.role,
                hasEmailVerified: authenticationResponse.user.hasEmailVerified,
            },
            token: {
                value: authenticationResponse.token,
                validUntil: this.prepareTokenValidUntil(authenticationResponse.tokenValidInMinutes)
            }
        }
    }
}

export default new AuthService();
